// ── BCHNostr — Promote Your Business (owner-facing) ───────────────
//
// Lets a VERIFIED business Page buy a "Sponsored" placement in the main
// feed, paid in BCH from its OWN in-app wallet. The ad event is signed by
// the business (the acting identity), so `businessPubkey === author` — the
// trust boundary the feed relies on.
//
// Reuses, never rebuilds:
//   · the in-app tip wallet (useTipWallet.sendBCH → PLATFORM_FEE_ADDRESS),
//   · the on-chain audit (paymentAudit via auditClaim) to CONFIRM payment
//     before we publish the paid ad event,
//   · the verification hook — a business must be verified to advertise.
//
// This section only renders for a business Page acting as itself.

import { useMemo, useState } from 'react';
import {
  Megaphone, Loader2, Wallet, BadgeCheck, AlertTriangle, Check, ShieldAlert, Clock,
} from 'lucide-react';
import { useTipWallet } from '../hooks/useTipWallet';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useBusinessProfile } from '../hooks/useBusinessProfile';
import { useBusinessVerification } from '../hooks/useBusinessVerification';
import { getBCHPrice } from '../hooks/useBCHPrice';
import { useBusinessAds } from '../hooks/useBusinessAds';
import { auditClaim } from '../lib/paymentAudit';
import { PLATFORM_FEE_ADDRESS } from '../lib/platform';
import {
  BUSINESS_AD_TIERS, BUSINESS_AD_HEADLINE_MAX,
  adUsdToSats, buildBusinessAdEvent, sanitizeHeadline,
  type BusinessAdTier,
} from '../lib/businessAd';

function fmtDate(unixSecs: number): string {
  try {
    return new Date(unixSecs * 1000).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return '—'; }
}

export default function PromoteBusinessSection({ pubkey }: { pubkey: string }) {
  const { publish } = useNostrPublish();
  const tipWallet = useTipWallet();
  const { business } = useBusinessProfile(pubkey);
  const verification = useBusinessVerification(pubkey);
  const { ads } = useBusinessAds(50); // includes this business's own active ad

  const [tierId, setTierId] = useState<BusinessAdTier['id']>('7d');
  const [headline, setHeadline] = useState('');
  const [step, setStep] = useState<'idle' | 'paying' | 'confirming' | 'publishing' | 'done' | 'error'>('idle');
  const [error, setError] = useState('');
  const [walletInitializing, setWalletInitializing] = useState(false);

  const tier = useMemo(
    () => BUSINESS_AD_TIERS.find(t => t.id === tierId) || BUSINESS_AD_TIERS[1],
    [tierId],
  );

  const price = getBCHPrice();
  const feeSats = adUsdToSats(tier.priceUsd, price) ?? 0;
  const walletBalance = Number(tipWallet.balanceSats);
  const canAfford = feeSats > 0 && walletBalance >= feeSats + 300;

  const isVerified = verification.status === 'VERIFIED';
  const myActiveAd = ads.find(a => a.businessPubkey === pubkey.toLowerCase());

  const busy = step === 'paying' || step === 'confirming' || step === 'publishing';

  const handleInitWallet = async () => {
    setWalletInitializing(true);
    try { await tipWallet.init(); } catch { /* surfaced below */ }
    setWalletInitializing(false);
  };

  const handlePay = async () => {
    if (!canAfford || feeSats <= 0 || !isVerified) return;
    setStep('paying');
    setError('');
    try {
      if (!tipWallet.ready) await tipWallet.init();

      // 1. Pay the ad fee to the platform wallet (existing rails).
      const txid = await tipWallet.sendBCH(PLATFORM_FEE_ADDRESS, BigInt(feeSats));

      // 2. Confirm on-chain before publishing the ad. Unconfirmed txs return
      //    'not-found' briefly; that's fine — the feed re-checks and gives a
      //    grace window, exactly like verification / boosts.
      setStep('confirming');
      if (txid) {
        const audit = await auditClaim(
          { name: pubkey, pubkey, claimedAt: Math.floor(Date.now() / 1000), txid, amountSats: feeSats },
          PLATFORM_FEE_ADDRESS,
          feeSats,
        );
        if (audit.verdict === 'wrong-recipient' || audit.verdict === 'underpaid') {
          throw new Error(
            audit.verdict === 'underpaid'
              ? 'The payment was for less than the ad price.'
              : 'The payment did not reach the ad address.',
          );
        }
      }

      // 3. Publish the ad event, signed by THIS business (the acting signer).
      setStep('publishing');
      const { kind, tags, content } = buildBusinessAdEvent({
        businessPubkey: pubkey,
        tier,
        priceSats: feeSats,
        bchPriceUsd: price,
        txid: txid || '',
        headline,
      });
      await publish({ kind, tags, content, created_at: Math.floor(Date.now() / 1000) });

      tipWallet.refreshBalance();
      setStep('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Payment failed. Please try again.');
      setStep('error');
    }
  };

  return (
    <div className="rounded-xl border border-boost-gold/40 bg-boost-gold/[0.05] p-4 space-y-3">
      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded-lg bg-boost-gold/15">
          <Megaphone className="w-4 h-4 text-boost-gold-ink" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold">Promote Your Business</h3>
          <p className="text-[11px] text-muted-foreground">
            Appear as a Sponsored card in the main feed. Verified businesses only.
          </p>
        </div>
      </div>

      {/* Gate: must be verified. */}
      {!isVerified && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2.5">
          <ShieldAlert className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-600 dark:text-amber-400">
            Only <strong>verified</strong> businesses can advertise. Verify your business above,
            then come back to promote it.
          </p>
        </div>
      )}

      {/* Current active ad, if any. */}
      {myActiveAd && (
        <div className="flex items-center gap-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2.5">
          <BadgeCheck className="w-5 h-5 text-emerald-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">Ad is live</p>
            <p className="text-[10px] text-muted-foreground flex items-center gap-1">
              <Clock className="w-3 h-3" /> Runs until {fmtDate(myActiveAd.data.expiresAt)}
            </p>
          </div>
        </div>
      )}

      {isVerified && step !== 'done' && (
        <div className="rounded-lg bg-background border border-border p-3 space-y-3">
          {/* Tier picker */}
          <div className="grid grid-cols-3 gap-2">
            {BUSINESS_AD_TIERS.map(t => {
              const active = t.id === tierId;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTierId(t.id)}
                  className={`rounded-xl border p-2.5 text-center transition-colors ${
                    active
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/40'
                  }`}
                >
                  <div className="text-base leading-none">{t.emoji}</div>
                  <div className="text-xs font-bold mt-1">{t.label}</div>
                  <div className={`text-[11px] font-mono font-bold mt-0.5 ${active ? 'text-primary' : 'text-muted-foreground'}`}>
                    ${t.priceUsd}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Optional headline */}
          <div>
            <label className="text-[11px] font-medium text-muted-foreground">
              Headline <span className="opacity-60">(optional)</span>
            </label>
            <input
              value={headline}
              onChange={e => setHeadline(sanitizeHeadline(e.target.value))}
              maxLength={BUSINESS_AD_HEADLINE_MAX}
              placeholder="A short pitch shown on your card"
              className="w-full bg-muted rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5 text-right">
              {headline.length}/{BUSINESS_AD_HEADLINE_MAX}
            </p>
          </div>

          {/* Price + pay */}
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Price</span>
            <span className="font-bold font-mono">
              ${tier.priceUsd} · {tier.label}{feeSats > 0 ? ` · ${feeSats.toLocaleString()} sats` : ''}
            </span>
          </div>

          {!tipWallet.ready ? (
            <button
              onClick={handleInitWallet}
              disabled={walletInitializing}
              className="w-full py-2 rounded-lg bg-muted text-xs font-medium flex items-center justify-center gap-1.5"
            >
              {walletInitializing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wallet className="w-3.5 h-3.5" />}
              {walletInitializing ? 'Loading…' : 'Load Wallet'}
            </button>
          ) : (
            <>
              <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                <span>Wallet balance</span>
                <span className={`font-mono font-bold ${canAfford ? 'text-emerald-500' : 'text-red-500'}`}>
                  {walletBalance.toLocaleString()} sats
                </span>
              </div>
              <button
                onClick={handlePay}
                disabled={busy || !canAfford}
                className={`w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                  canAfford
                    ? 'bg-gradient-to-r from-primary to-emerald-500 text-white hover:opacity-90'
                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                } disabled:opacity-50`}
              >
                {step === 'paying' ? <><Loader2 className="w-4 h-4 animate-spin" /> Paying…</>
                  : step === 'confirming' ? <><Loader2 className="w-4 h-4 animate-spin" /> Confirming…</>
                  : step === 'publishing' ? <><Loader2 className="w-4 h-4 animate-spin" /> Publishing…</>
                  : <><Megaphone className="w-4 h-4" /> {myActiveAd ? 'Extend / Re-promote' : 'Promote'} for ${tier.priceUsd}</>}
              </button>
              {!canAfford && feeSats > 0 && (
                <p className="text-[10px] text-red-500">Not enough funds. Deposit to your wallet first.</p>
              )}
              {feeSats <= 0 && (
                <p className="text-[10px] text-amber-500">Fetching the current BCH price…</p>
              )}
            </>
          )}
        </div>
      )}

      {step === 'done' && (
        <div className="flex items-start gap-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-3 py-2.5">
          <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
              Your ad is published!
            </p>
            <p className="text-[10px] text-muted-foreground">
              It appears as a Sponsored card in the feed for {tier.label.toLowerCase()}, once the
              payment confirms on-chain (usually a few minutes).
            </p>
          </div>
        </div>
      )}

      {step === 'error' && error && (
        <p className="flex items-start gap-1.5 text-xs text-red-500">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {error}
        </p>
      )}

      <p className="text-[10px] text-muted-foreground leading-relaxed">
        Sponsored cards are clearly labeled as paid placements — not organic recommendations. Your
        ad runs for the period you choose and stops automatically. Ads only show while your business
        stays verified.
      </p>
    </div>
  );
}
